import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Users, Award, Calendar, TrendingUp, Target, Plus, MapPin, Trophy, RefreshCw, Briefcase, MessageCircle } from 'lucide-react';
import { userAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const CoachDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const loadProfile = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await userAPI.getMyProfile();
      setProfile(response.data);
    } catch (error) {
      if (!silent) toast.error('Failed to load profile');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    // Auto-refresh every 15 seconds
    const refreshInterval = setInterval(() => loadProfile(true), 15000);
    return () => clearInterval(refreshInterval);
  }, [loadProfile]);

  const stats = profile?.stats || {};

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="coach-dashboard">
      {/* Header */}
      <motion.div 
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">Coach Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your athletes and training programs
            </p>
          </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
            <Button onClick={() => navigate('/trials')} className="btn-primary" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Post Trial
            </Button>
            <Button onClick={() => navigate('/jobs')} variant="outline" size="sm">
              <Briefcase className="w-4 h-4 mr-2" />
              Post Job
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Profile Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5"
      >
        <Card className="card-premium overflow-hidden border-0">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Avatar className="w-16 h-16 ring-2 ring-primary/20 ring-offset-2 ring-offset-background flex-shrink-0">
                <AvatarImage src={currentUser.profileImage} alt={currentUser.name} />
                <AvatarFallback className="text-lg font-bold bg-primary text-primary-foreground">
                  {currentUser.name?.[0] || 'C'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-foreground truncate">{currentUser.name}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                    <Trophy className="w-3 h-3 mr-1.5 text-primary" />
                    {currentUser.sports?.[0] || 'Multi-Sport'}
                  </span>
                  <span className="inline-flex items-center text-xs text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                    <MapPin className="w-3 h-3 mr-1.5" />
                    {currentUser.location || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
      >
        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Athletes Coached</p>
                <h3 className="stat-value mt-1">{stats.athletesCoached || 0}</h3>
              </div>
              <div className="icon-container-purple">
                <Users className="w-5 h-5 text-primary" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Championships</p>
                <h3 className="stat-value mt-1">{stats.championships || 0}</h3>
              </div>
              <div className="icon-container-orange">
                <Award className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Experience</p>
                <h3 className="stat-value mt-1">{stats.yearsExperience || 0}y</h3>
              </div>
              <div className="icon-container-green">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div variants={cardVariants}>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Success Rate</p>
                <h3 className="stat-value mt-1">92%</h3>
              </div>
              <div className="icon-container-blue">
                <Target className="w-5 h-5 text-primary" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Target className="w-4 h-4 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0 space-y-3">
              <Button 
                className="w-full btn-primary justify-start" 
                onClick={() => navigate('/trials')}
              >
                <Plus className="w-4 h-4 mr-3" />
                Post Trial Event
              </Button>
              <Button 
                className="w-full btn-primary justify-start" 
                onClick={() => navigate('/jobs')}
              >
                <Briefcase className="w-4 h-4 mr-3" />
                Post Job Opening
              </Button>
              <Button 
                variant="outline"
                className="w-full justify-start hover-lift" 
                onClick={() => navigate('/discover')}
              >
                <Users className="w-4 h-4 mr-3" />
                Discover Athletes
              </Button>
              <Button 
                variant="outline"
                className="w-full justify-start hover-lift" 
                onClick={() => navigate('/messages')}
              >
                <MessageCircle className="w-4 h-4 mr-3" />
                Messages
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Award className="w-4 h-4 text-primary" />
                Certifications & Qualifications
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              {stats.certifications && (Array.isArray(stats.certifications) ? stats.certifications.length > 0 : stats.certifications) ? (
                <div className="space-y-2">
                  {(Array.isArray(stats.certifications) ? stats.certifications : (typeof stats.certifications === 'string' ? stats.certifications.split('\n') : [])).map((cert, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <p className="text-sm text-foreground flex items-start">
                        <Award className="w-4 h-4 mr-2 mt-0.5 text-primary flex-shrink-0" />
                        {typeof cert === 'object' ? `${cert.name}${cert.issuer ? ` - ${cert.issuer}` : ''}${cert.year ? ` (${cert.year})` : ''}` : cert}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground mb-3">No certifications added yet</p>
                  <Button variant="outline" size="sm" onClick={() => navigate('/profile')}>
                    Add Certifications
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Upcoming Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Calendar className="w-4 h-4 text-primary" />
                Upcoming Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">No upcoming sessions</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Card className="card-premium h-full">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <TrendingUp className="w-4 h-4 text-primary" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="text-center py-8">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">No recent activity</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default CoachDashboard;
